"use client";

import { useCallback, useEffect, useState } from "react";
import { isMobileDevice } from "@/lib/utils";

type ShareJobConfig = {
  title?: string;
  text?: string;
  url?: string;
  forceFallback?: boolean;
};


export const useShareJob = ({ title, text, url, forceFallback }: ShareJobConfig) => {
  const [isOpen, setIsOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(url ?? "");

  useEffect(() => {
    if (url) {
      setShareUrl(url);
    }
  }, [url]);

  const closeShare = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openShare = useCallback(async (optionsOrEvent?: ShareJobConfig | React.UIEvent | React.MouseEvent | Event) => {
    const isEvent = optionsOrEvent && ('nativeEvent' in optionsOrEvent || 'preventDefault' in optionsOrEvent);
    const overrides = isEvent ? undefined : (optionsOrEvent as ShareJobConfig);

    // Use the provided shareUrl directly - no fallback to window.location.href
    const resolvedUrl = overrides?.url ?? shareUrl;
    const resolvedTitle = overrides?.title ?? title;
    const resolvedText = overrides?.text ?? text;

    if (!resolvedUrl) {
      console.warn("No share URL provided to useShareJob");
      return;
    }

    const forceFallbackEnabled =
      typeof window !== "undefined" &&
      (() => {
        const params = new URLSearchParams(window.location.search);
        const value = params.get("shareFallback");
        return value === "1" || value === "true" || window.location.href.includes("shareFallback=1");
      })();

    // Default to forcing our custom popup on Desktop ("website") even if navigator.share exists (e.g. Safari Mac)
    // On Mobile, try native share first.
    const isMobile = isMobileDevice();
    const shouldFallback = forceFallback ?? (forceFallbackEnabled || !isMobile);

    if (!shouldFallback && typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: resolvedTitle,
          text: resolvedText,
          url: resolvedUrl,
        });
        return;
      } catch (e) {
        // User cancelled the native share dialog — don't open custom sheet
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        // Actual share error — fall through to custom sheet
      }
    }

    setShareUrl(resolvedUrl);
    setIsOpen(true);
  }, [shareUrl, text, title, forceFallback]);

  return {
    isOpen,
    shareUrl,
    openShare,
    closeShare,
  };
};
